'use client';

import { useState, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Copy, RefreshCw, Check } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { randomStringGeneratorContent } from '../../data/random-string-generator-content';
import Link from 'next/link';

type CharacterSet = {
    id: string;
    label: string;
    value: string;
    default: boolean;
};

const CHARACTER_SETS: CharacterSet[] = [
    { id: 'lowercase', label: 'a-z', value: 'abcdefghijklmnopqrstuvwxyz', default: true },
    { id: 'uppercase', label: 'A-Z', value: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ', default: true },
    { id: 'numbers', label: '0-9', value: '0123456789', default: true },
    { id: 'symbols', label: '!@#$%^&*', value: '!@#$%^&*()_+-=[]{}|;:,.<>?', default: false },
    { id: 'hex', label: 'Hex (0-9, A-F)', value: '0123456789ABCDEF', default: false },
    { id: 'binary', label: 'Binary (0-1)', value: '01', default: false },
];

const DEFAULT_LENGTH = 16;
const MIN_LENGTH = 1;
const MAX_LENGTH = 1024;

const generateRandomString = (
    length: number,
    characterSets: CharacterSet[],
    selectedSets: string[]
): string => {
    // If no sets are selected, use the default ones
    const activeSets = selectedSets.length > 0
        ? characterSets.filter(set => selectedSets.includes(set.id))
        : characterSets.filter(set => set.default);

    if (activeSets.length === 0) {
        return '';
    }

    // Combine all selected character sets
    const allChars = activeSets.map(set => set.value).join('');

    // Generate the random string
    let result = '';
    const crypto = window.crypto || (window as any).msCrypto; // For IE11
    const values = new Uint32Array(length);

    crypto.getRandomValues(values);

    for (let i = 0; i < length; i++) {
        result += allChars[values[i] % allChars.length];
    }

    return result;
};

export default function RandomStringGenerator() {
    const [length, setLength] = useState<number>(DEFAULT_LENGTH);
    const [result, setResult] = useState<string>('');
    const [copied, setCopied] = useState<boolean>(false);
    const [selectedSets, setSelectedSets] = useState<string[]>(
        CHARACTER_SETS.filter(set => set.default).map(set => set.id)
    );
    const [customChars, setCustomChars] = useState<string>('');
    const [useCustomChars, setUseCustomChars] = useState<boolean>(false);
    const [isGenerating, setIsGenerating] = useState<boolean>(false);
    const { toast } = useToast();

    const generateString = useCallback(() => {
        setIsGenerating(true);

        // Small delay to show loading state
        setTimeout(() => {
            try {
                let newResult = '';

                if (useCustomChars && customChars) {
                    // Use only custom characters
                    newResult = Array.from(
                        { length },
                        () => customChars[Math.floor(Math.random() * customChars.length)]
                    ).join('');
                } else {
                    // Use selected character sets
                    newResult = generateRandomString(length, CHARACTER_SETS, selectedSets);
                }

                setResult(newResult);
            } catch (error) {
                console.error('Error generating string:', error);
                toast({
                    title: 'Error',
                    description: 'Failed to generate random string. Please try again.',
                    variant: 'destructive',
                });
            } finally {
                setIsGenerating(false);
            }
        }, 100);
    }, [length, selectedSets, customChars, useCustomChars, toast]);

    const handleCopy = useCallback(() => {
        if (!result) return;

        navigator.clipboard.writeText(result);
        setCopied(true);
        toast({
            title: 'Copied!',
            description: 'Random string has been copied to clipboard.',
        });

        setTimeout(() => setCopied(false), 2000);
    }, [result, toast]);

    const toggleCharacterSet = (id: string) => {
        setSelectedSets(prev =>
            prev.includes(id)
                ? prev.filter(setId => setId !== id)
                : [...prev, id]
        );
    };

    // Generate initial string on mount
    useEffect(() => {
        generateString();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="container mx-auto py-8 px-4">
            <div className="max-w-3xl mx-auto">
                <div className="mb-8 text-center">
                    <ToolPageIntro title="Random String Generator">
                Generate random strings with our free online tool. Create secure passwords, unique identifiers, and random text for development and testing.
            </ToolPageIntro>
                </div>

                <div className="grid gap-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Configuration</CardTitle>
                            <CardDescription>Customize your random string</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="space-y-2">
                                <div className="flex justify-between items-center">
                                    <Label htmlFor="length">Length: {length}</Label>
                                    <div className="text-sm text-muted-foreground">
                                        {MIN_LENGTH} - {MAX_LENGTH} characters
                                    </div>
                                </div>
                                <div className="px-2">
                                    <Slider
                                        id="length"
                                        min={MIN_LENGTH}
                                        max={MAX_LENGTH}
                                        step={1}
                                        value={[length]}
                                        onValueChange={([value]) => setLength(value)}
                                        className="py-4"
                                    />
                                </div>
                            </div>

                            <div className="space-y-4">
                                <Tabs defaultValue="presets" className="w-full">
                                    <TabsList className="grid w-full grid-cols-2">
                                        <TabsTrigger value="presets">Presets</TabsTrigger>
                                        <TabsTrigger value="custom">Custom Characters</TabsTrigger>
                                    </TabsList>

                                    <TabsContent value="presets" className="pt-4">
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                            {CHARACTER_SETS.map(set => (
                                                <div key={set.id} className="flex items-center space-x-2">
                                                    <Switch
                                                        id={set.id}
                                                        checked={selectedSets.includes(set.id)}
                                                        onCheckedChange={() => toggleCharacterSet(set.id)}
                                                    />
                                                    <Label htmlFor={set.id} className="font-normal">
                                                        {set.label} ({set.value.length} chars)
                                                    </Label>
                                                </div>
                                            ))}
                                        </div>
                                    </TabsContent>

                                    <TabsContent value="custom" className="pt-4">
                                        <div className="space-y-4">
                                            <div className="space-y-2">
                                                <Label htmlFor="custom-chars">Custom Characters</Label>
                                                <Input
                                                    id="custom-chars"
                                                    value={customChars}
                                                    onChange={(e) => setCustomChars(e.target.value)}
                                                    placeholder="Enter custom characters (e.g., ABC123!@#)"
                                                />
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <Switch
                                                    id="use-custom"
                                                    checked={useCustomChars}
                                                    onCheckedChange={setUseCustomChars}
                                                />
                                                <Label htmlFor="use-custom" className="font-normal">
                                                    Use custom characters only
                                                </Label>
                                            </div>
                                            {useCustomChars && customChars.length === 0 && (
                                                <p className="text-sm text-destructive">
                                                    Please enter at least one character
                                                </p>
                                            )}
                                        </div>
                                    </TabsContent>
                                </Tabs>
                            </div>

                            <div className="pt-2">
                                <Button
                                    onClick={generateString}
                                    disabled={isGenerating || (useCustomChars && customChars.length === 0)}
                                    className="w-full sm:w-auto"
                                >
                                    {isGenerating ? (
                                        <>
                                            <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                                            Generating...
                                        </>
                                    ) : (
                                        <>
                                            <RefreshCw className="mr-2 h-4 w-4" />
                                            Generate New String
                                        </>
                                    )}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="pb-2">
                            <div className="flex justify-between items-center">
                                <CardTitle>Generated String</CardTitle>
                                <div className="flex items-center space-x-2">
                                    <div className="text-sm text-muted-foreground">
                                        {result.length} {result.length === 1 ? 'character' : 'characters'}
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleCopy}
                                        disabled={!result}
                                    >
                                        {copied ? (
                                            <Check className="h-4 w-4 mr-2" />
                                        ) : (
                                            <Copy className="h-4 w-4 mr-2" />
                                        )}
                                        {copied ? 'Copied!' : 'Copy'}
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="relative">
                                <pre className="bg-muted/50 p-4 rounded-md overflow-x-auto text-sm">
                                    <code>{result || '// Click generate to create a random string'}</code>
                                </pre>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="mt-8 text-center text-sm text-muted-foreground">
                    <p>
                        This tool generates cryptographically secure random strings using the Web Crypto API.
                        No data is sent to any server - all generation happens in your browser.
                    </p>
                </div>
            </div>

            {/* Informational Content */}
            <InformationalContent
                title={randomStringGeneratorContent.title}
                description={randomStringGeneratorContent.description}
                content={randomStringGeneratorContent.content}
                faqs={randomStringGeneratorContent.faqs}
                relatedTools={randomStringGeneratorContent.relatedTools}
            />
        </div>
    );
}
